// Runtime Web App Manifest.
//
// buildManifest() (manifest.config.js) bakes a manifest at build time from the
// industry JSON — but the tenant's real name and logo only arrive at runtime
// from the resolve-by-domain API (runtimeBrandStore), so every tenant on a
// shared build would otherwise install as "Bytz Commerce".
//
// This module rebuilds the manifest from the runtime brand and hands it to the
// service worker (public/sw-brand-manifest.js), which serves it at the real
// /manifest.webmanifest URL. Chrome's manifest fetch does go through the SW —
// verified via CDP Page.getAppManifest.
//
// Why not swap link[rel=manifest] to a blob: URL (the common trick)? It races
// SW registration, and a blob has no origin to resolve start_url/scope against.
// Keeping a real same-origin URL sidesteps both.
//
// The build-time manifest remains the fallback: it is what the browser reads
// on a first visit before the SW is in control, and offline.

const MANIFEST_PATH = '/manifest.webmanifest';
const CONTROLLER_TIMEOUT_MS = 10_000;

const MIME_BY_EXTENSION = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
  webp: 'image/webp',
  ico: 'image/x-icon',
};

function iconType(url) {
  const extension = String(url).split(/[#?]/)[0].split('.').pop()?.toLowerCase();
  return MIME_BY_EXTENSION[extension] || 'image/png';
}

function absoluteUrl(url) {
  if (!url) return '';
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return '';
  }
}

function brandIcons(logoUrl) {
  const src = absoluteUrl(logoUrl);
  if (!src) return null;
  const type = iconType(src);
  // The logo is a single asset of unknown pixel size. Declaring 192/512 lets
  // Chrome treat it as install-eligible (it requires a >=144px entry); the
  // maskable copy keeps Android from framing it in a white square.
  return [
    { src, sizes: '192x192', type, purpose: 'any' },
    { src, sizes: '512x512', type, purpose: 'any' },
    { src, sizes: '512x512', type, purpose: 'maskable' },
  ];
}

/**
 * Merge the build-time manifest with the runtime brand.
 * @param {object} params
 * @param {object} [params.baseManifest] Build-time manifest (fallback values + icons).
 * @param {string} [params.brandName]    Tenant business name from resolve.
 * @param {string} [params.logoUrl]      Tenant logo URL from resolve.
 * @param {string} [params.description]
 * @param {string} [params.themeColor]
 * @param {string} [params.backgroundColor]
 */
export function buildRuntimeManifest({
  baseManifest = {},
  brandName = '',
  logoUrl = '',
  description = '',
  themeColor = '',
  backgroundColor = '',
} = {}) {
  const name = brandName || baseManifest.name || '';
  const icons = brandIcons(logoUrl);

  return {
    ...baseManifest,
    name,
    // Home-screen labels truncate around 12 chars; keep the first word rather
    // than letting the OS clip mid-word.
    short_name: name.split(' ')[0] || baseManifest.short_name || name,
    description: description || baseManifest.description || '',
    theme_color: themeColor || baseManifest.theme_color || '#ffffff',
    background_color: backgroundColor || baseManifest.background_color || '#ffffff',
    display: baseManifest.display || 'standalone',
    orientation: baseManifest.orientation || 'portrait',
    start_url: absoluteUrl(baseManifest.start_url || '/'),
    scope: absoluteUrl(baseManifest.scope || '/'),
    icons: icons || (baseManifest.icons || []).map((i) => ({ ...i, src: absoluteUrl(i.src) })),
  };
}

/**
 * Read the build-time manifest, so runtime values layer over it.
 * Fetches the path directly rather than the link's href, which the nudge
 * rewrites. The SW deliberately lets this request through to the static file
 * (it only intercepts destination === 'manifest').
 */
export async function readBaseManifest() {
  try {
    const response = await fetch(MANIFEST_PATH);
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

let nudgeCount = 0;

// A SW cannot control the page load that registered it, so on a first visit
// Chrome has already read the static manifest. Re-pointing the link at the
// same path with a fresh query forces a re-fetch — now served by the SW. The
// path is unchanged, so app identity (derived from start_url) is unaffected.
function nudgeManifestLink() {
  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'manifest');
    document.head.appendChild(link);
  }
  // The href must actually change for Chrome to re-read it.
  nudgeCount += 1;
  link.setAttribute('href', `${MANIFEST_PATH}?brand=${nudgeCount}`);
}

// On a first visit the SW registers (lazily, via SwUpdatePrompt) and claims the
// page some time after this module first runs, so waiting for the controller is
// the difference between branding the first install and missing it.
function waitForController() {
  const { serviceWorker } = navigator;
  if (serviceWorker.controller) return Promise.resolve(serviceWorker.controller);

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      serviceWorker.removeEventListener('controllerchange', onChange);
      resolve(null);
    }, CONTROLLER_TIMEOUT_MS);

    function onChange() {
      clearTimeout(timer);
      serviceWorker.removeEventListener('controllerchange', onChange);
      resolve(serviceWorker.controller);
    }
    serviceWorker.addEventListener('controllerchange', onChange);
  });
}

/**
 * Hand the manifest to the service worker, then make Chrome re-read it.
 * No-op where no SW ever takes control (dev server, unsupported browser) —
 * the build-time manifest stays in place.
 * @returns {Promise<boolean>} whether the SW stored it.
 */
export async function applyRuntimeManifest(manifest) {
  if (!manifest?.name || !navigator.serviceWorker) return false;

  const worker = await waitForController();
  if (!worker) return false;

  // Wait for the SW to confirm the write before nudging: a re-fetch that beats
  // the cache put would serve the static manifest and not be retried.
  const stored = await new Promise((resolve) => {
    const timer = setTimeout(() => finish(false), CONTROLLER_TIMEOUT_MS);
    function finish(ok) {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('message', onMessage);
      resolve(ok);
    }
    function onMessage(event) {
      if (event.data?.type === 'BRAND_MANIFEST_STORED') finish(true);
    }
    navigator.serviceWorker.addEventListener('message', onMessage);
    worker.postMessage({ type: 'SET_BRAND_MANIFEST', manifest });
  });

  if (stored) nudgeManifestLink();
  return stored;
}
